/**
 * Centralized Environment Configuration
 * This file manages all environment-specific settings for the CardMint application
 */

import { Capacitor } from '@capacitor/core';

// Environment detection
export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';

// Platform detection
export const isNative = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isAndroid = Capacitor.getPlatform() === 'android';
export const isWeb = Capacitor.getPlatform() === 'web';

// API Configuration
// Use absolute URL for mobile, relative for web
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Mobile apps need absolute URL
  if (isNative) {
    return 'https://cardmint.ai/api';
  }
  
  // Web can use relative URL
  return '/api';
};

export const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10), // 30 seconds
  retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS || '3', 10)
};

// Frontend Configuration
export const FRONTEND_CONFIG = {
  url: process.env.REACT_APP_FRONTEND_URL || (
    isProduction ? 'https://cardmint.ai' : 'http://localhost:3000'
  ),
  appName: 'CardMint',
  version: process.env.REACT_APP_VERSION || '1.0.0'
};

// Stripe Configuration
export const STRIPE_CONFIG = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SD5TQCPgKO8dcVwkrhz8xJ8G9N37MKLocRqpeRRswPMrhXvJdotBNv4ZVB2pZBJd7CehiBOGXudMnNTNwdh1MFt00iJVaKXzp'
};

// Feature Flags
export const FEATURES = {
  enableVideoLoading: process.env.REACT_APP_ENABLE_VIDEO_LOADING !== 'false', // Default true
  enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS !== 'false', // Default true
  enableRealtime: process.env.REACT_APP_ENABLE_REALTIME !== 'false', // Default true
  enableDebugLogs: isDevelopment && process.env.REACT_APP_ENABLE_DEBUG_LOGS !== 'false',
  maintenanceMode: process.env.REACT_APP_MAINTENANCE_MODE === 'true' // Default false
};

// UI Configuration
export const UI_CONFIG = {
  maxFileSize: parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '10485760', 10), // 10MB
  supportedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  cardGenerationTimeout: parseInt(process.env.REACT_APP_CARD_GENERATION_TIMEOUT || '45000', 10), // 45 seconds
  videoLoadingMinDuration: parseInt(process.env.REACT_APP_VIDEO_MIN_DURATION || '30000', 10), // 30 seconds
  mobileBreakpoint: parseInt(process.env.REACT_APP_MOBILE_BREAKPOINT || '768', 10)
};

// Rate Limiting (for client-side awareness)
export const RATE_LIMITS = {
  cardGeneration: {
    requests: parseInt(process.env.REACT_APP_RATE_LIMIT_CARDS || '5', 10),
    windowMs: parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW || '60000', 10) // 1 minute
  },
  authentication: {
    requests: parseInt(process.env.REACT_APP_RATE_LIMIT_AUTH || '10', 10),
    windowMs: parseInt(process.env.REACT_APP_RATE_LIMIT_AUTH_WINDOW || '60000', 10) // 1 minute
  }
};

// Logging Configuration
export const LOGGING_CONFIG = {
  level: process.env.REACT_APP_LOG_LEVEL || (isProduction ? 'error' : 'debug'),
  enableConsole: isDevelopment || process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true',
  enableRemote: isProduction && process.env.REACT_APP_ENABLE_REMOTE_LOGGING === 'true'
};

// Export all configurations as a single object
export const config = {
  isDevelopment,
  isProduction,
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  api: API_CONFIG,
  frontend: FRONTEND_CONFIG,
  stripe: STRIPE_CONFIG,
  features: FEATURES,
  ui: UI_CONFIG,
  rateLimits: RATE_LIMITS,
  logging: LOGGING_CONFIG
};

export default config;
